/**
 * HSL triple ↔ hex conversion. BrandConfig stores colors as space-
 * separated HSL triples ("221 83% 53%") because Tailwind's theme
 * system consumes them that way. Admins think in hex, so the
 * BrandEditor shows a native color picker whose value is hex; we
 * convert on every edit.
 *
 * Invariants: round-trips lossy within ~1% on each channel (hex
 * quantization). Undefined/empty/malformed input returns sentinel
 * values rather than throwing - the caller uses the triple to
 * render a preview + a hex color input to edit, and both degrade
 * gracefully when the underlying field is blank ("inherit").
 */

const HSL_TRIPLE_RE = /^\s*(\d{1,3}(?:\.\d+)?)\s+(\d{1,3}(?:\.\d+)?)%\s+(\d{1,3}(?:\.\d+)?)%\s*$/;

export function isHslTriple(value: string): boolean {
  return HSL_TRIPLE_RE.test(value);
}

export function hslTripleToHex(triple: string): string {
  const m = triple.match(HSL_TRIPLE_RE);
  if (!m) return "#000000";
  const h = Number(m[1]) % 360;
  const s = Math.min(100, Math.max(0, Number(m[2]))) / 100;
  const l = Math.min(100, Math.max(0, Number(m[3]))) / 100;
  // Standard HSL → RGB (https://www.w3.org/TR/css-color-3/#hsl-color).
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hp < 1) [r1, g1, b1] = [c, x, 0];
  else if (hp < 2) [r1, g1, b1] = [x, c, 0];
  else if (hp < 3) [r1, g1, b1] = [0, c, x];
  else if (hp < 4) [r1, g1, b1] = [0, x, c];
  else if (hp < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];
  const m0 = l - c / 2;
  const r = Math.round((r1 + m0) * 255);
  const g = Math.round((g1 + m0) * 255);
  const b = Math.round((b1 + m0) * 255);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

export function hexToHslTriple(hex: string): string {
  const m = /^#?([\da-f]{6})$/i.exec(hex.trim());
  if (!m) return "";
  const intVal = parseInt(m[1]!, 16);
  const r = ((intVal >> 16) & 0xff) / 255;
  const g = ((intVal >> 8) & 0xff) / 255;
  const b = (intVal & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
    else if (max === g) h = ((b - r) / d + 2) * 60;
    else h = ((r - g) / d + 4) * 60;
  }
  const hRound = Math.round(h);
  const sRound = Math.round(s * 100);
  const lRound = Math.round(l * 100);
  return `${hRound} ${sRound}% ${lRound}%`;
}

/** Accept either an HSL triple ("221 83% 53%") or a hex string
 *  ("#aabbcc"). Returns a CSS color value suitable for
 *  `style={{ backgroundColor: ... }}`. Undefined on empty. */
export function anyColorToCss(value: string): string | undefined {
  if (!value) return undefined;
  if (isHslTriple(value)) return `hsl(${value})`;
  if (/^#[\da-f]{6}$/i.test(value.trim())) return value.trim();
  return undefined;
}

// --------------------------------------------------------------------- //
// WCAG 2.1 contrast helpers                                             //
// --------------------------------------------------------------------- //
// Used by BrandEditor to warn admins when picked color pairs (primary↔
// primary text, background↔foreground) fall below readable contrast.
// Math from https://www.w3.org/TR/WCAG21/#contrast-minimum.
//
// AAA pass:        ratio ≥ 7
// AA pass:         ratio ≥ 4.5  (normal text)
// AA large text:   ratio ≥ 3
// Fail (block):    ratio <  3   - text becomes unreadable for cashiers
//                                  who often work at angled / glared
//                                  monitors. Save gate uses this floor.

function srgbChannelToLinear(c: number): number {
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function hexToRgbNorm(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([\da-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const intVal = parseInt(m[1]!, 16);
  return {
    r: ((intVal >> 16) & 0xff) / 255,
    g: ((intVal >> 8) & 0xff) / 255,
    b: (intVal & 0xff) / 255,
  };
}

/** Relative luminance per WCAG 2.1. Accepts either an HSL triple or
 *  a hex string. Returns null when the input doesn't parse. */
export function relativeLuminance(value: string): number | null {
  const hex = isHslTriple(value) ? hslTripleToHex(value) : value;
  const rgb = hexToRgbNorm(hex);
  if (!rgb) return null;
  const r = srgbChannelToLinear(rgb.r);
  const g = srgbChannelToLinear(rgb.g);
  const b = srgbChannelToLinear(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.1 contrast ratio (1..21) between two colors. Returns null
 *  when either input doesn't parse. Order doesn't matter. */
export function contrastRatio(a: string, b: string): number | null {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la == null || lb == null) return null;
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/** WCAG-classified bucket for a contrast ratio. */
export type ContrastTier = "fail" | "large-only" | "aa" | "aaa";

export function classifyContrast(ratio: number): ContrastTier {
  if (ratio >= 7) return "aaa";
  if (ratio >= 4.5) return "aa";
  if (ratio >= 3) return "large-only";
  return "fail";
}
